<template>
  <div
    class="min-h-[24px] gap-8 items-center flex cursor-pointer"
    role="presentation"
    @click="emit('toggle')"
  >
    <div class="w-12 flex">
      <button
        v-if="hasChildren"
        type="button"
        class="bitts-tree__toggle"
        :aria-expanded="isExpanded"
        :aria-label="`${isExpanded ? 'Collapse' : 'Expand'} ${accessibleName ?? node.label}`"
        :disabled="isToggleLocked"
        @click.stop="emit('toggle')"
      >
        <FontAwesomeIcon
          :icon="['fak', 'chevron-right']"
          :style="{ height: '12px', width: '12px', color: 'currentColor' }"
          class="bitts-tree__chevron"
          :class="{
            'text-neutral-accent': !isExpanded,
            'rotate-90': isExpanded,
          }"
        />
      </button>
    </div>
    <template v-if="node.isCheckable !== false">
      <slot
        name="node-checkbox"
        :node="node"
        :checked="checked"
        :indeterminate="indeterminate"
        :on-input="(value: boolean) => emit('check', value)"
        :disabled="node.isDisabled || isDisabled"
      >
        <BittsCheckbox
          :checked="checked"
          :indeterminate="indeterminate"
          :aria-label="accessibleName ?? node.label"
          @input="(value) => emit('check', value)"
          @click.stop
          :disabled="node.isDisabled || isDisabled"
        />
      </slot>
    </template>
    <slot name="node-prefix" :node="node"></slot>
    <slot name="node-content" :node="node" :highlight-term="highlightTerm">
      <BittsTreeNodeLabel :label="node.label" :highlight-term="highlightTerm" />
    </slot>
    <slot name="node-suffix" :node="node"></slot>
  </div>
</template>

<script setup lang="ts" generic="TMetaData">
import BittsCheckbox from '../BittsCheckbox/BittsCheckbox.vue';

import type { TreeNodeType } from './BittsBaseTree.vue';
import BittsTreeNodeLabel from './BittsTreeNodeLabel.vue';

type Node = TreeNodeType<TMetaData>;

const {
  node,
  checked,
  indeterminate,
  hasChildren,
  isExpanded,
  isToggleLocked,
  isDisabled,
  highlightTerm,
  accessibleName,
} = defineProps<{
  node: Node;
  checked: boolean | undefined;
  indeterminate: boolean;
  hasChildren: boolean;
  isExpanded: boolean;
  isToggleLocked: boolean;
  isDisabled: boolean;
  highlightTerm: string;
  accessibleName?: string;
}>();

const emit = defineEmits<{
  toggle: [];
  check: [value: boolean];
}>();

defineSlots<{
  'node-checkbox'?(props: {
    node: Node;
    checked: boolean | undefined;
    indeterminate: boolean;
    onInput: (checked: boolean) => void;
    disabled: boolean;
  }): unknown;
  'node-prefix'?(props: { node: Node }): unknown;
  'node-content'?(props: { node: Node; highlightTerm: string }): unknown;
  'node-suffix'?(props: { node: Node }): unknown;
}>();
</script>

<style scoped lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.bitts-tree__toggle {
  @apply relative inline-flex items-center justify-center appearance-none border-0 bg-transparent p-0 cursor-pointer;
}

.bitts-tree__toggle::before {
  content: '';
  @apply absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2;
}

.bitts-tree__toggle:disabled {
  @apply cursor-not-allowed;
}

.bitts-tree__toggle:focus-visible {
  @apply outline-2 outline-offset-2 outline-neutral-border-focus rounded-4;
}

.bitts-tree__chevron {
  @apply ml-4 transition-transform duration-150;
}
</style>
